import { supabase } from "../supabase/SupabaseClient";

/* Orden de gas oil (ticket 431): los choferes para elegir y el historial.
 *
 * Los choferes vienen de `orden_gasoil_choferes()`, con su tractor asignado
 * (`chofer_tractor`) y los datos del tractor. El teléfono llega sólo con
 * `ver.telefono_chofer`: lo decide la base, no esta pantalla. */

export async function choferes() {
  const { data, error } = await supabase.rpc("orden_gasoil_choferes");
  if (error) {
    console.warn("[orden-gasoil] choferes:", error.message);
    return { filas: [], error: "No se pudo consultar." };
  }
  return { filas: data || [], error: "" };
}

const CAMPOS = "id,creado_en,usuario,usuario_nombre,dni,chofer,interno,dominio,ypf,empresa_nombre,litros,motivo";
/* Con el cambio de litros autorizado (Lautaro y Nico, 21/10): lo pedido antes
 * del cambio, quién lo pidió y quién lo autorizó. Si la base todavía no tiene
 * esas columnas se lee con `CAMPOS`. */
const CAMPOS_EDITADOS = `${CAMPOS},tanque_completo,litros_pedidos,litros_editados_en,litros_editados_por,litros_autorizados_por`;
const CAMPOS_CAMBIO = "id,orden_id,litros_antes,litros_nuevos,motivo,pedido_por,pedido_por_nombre,pedido_en,estado,resuelto_por,resuelto_en";

/* ¿La base todavía no tiene `borrada_en` (la migración del ticket 448 sin
 * aplicar)? Entonces no hay ninguna borrada y el historial se lee como antes. */
const faltaBorrada = (error) =>
  error?.code === "42703" ||
  (/borrada_en/.test(error?.message || "") && /column|schema cache|does not exist|could not find/i.test(error?.message || ""));

/* ¿La base todavía no tiene las columnas del cambio de litros (la migración
 * 20261023172118 sin aplicar)? Se mira por el nombre: un 42703 solo puede ser
 * `borrada_en`, que es más vieja. */
const faltaEditados = (error) =>
  /litros_pedidos|litros_editados|litros_autorizados|tanque_completo/.test(error?.message || "") &&
  (error?.code === "42703" || /column|schema cache|does not exist|could not find/i.test(error?.message || ""));

/* PGRST202: PostgREST no conoce la función · 42883: Postgres tampoco. */
const faltaFuncion = (error, nombre = "borrar_orden_gasoil") =>
  ["PGRST202", "42883"].includes(error?.code) ||
  new RegExp(`could not find the function|function .*${nombre}.* does not exist`, "i").test(error?.message || "");

/* El historial NO trae las borradas (ticket 448). El filtro va acá y no en la
 * política: quien tiene `cargar.satelital` puede seguir leyendo, desde la
 * base, qué se borró, cuándo y quién, que es para lo que se marca en vez de
 * eliminar. */
/* Con `rango` ({ desde, hasta }: instantes ISO, de `limitesDelRango`), sólo
 * las de esas fechas, hasta `TOPE_RANGO` (ticket 561: el ranking de quién pide
 * más es de las fechas que se miran, no de las últimas 50). */
export const TOPE_RANGO = 1000;

export async function historial(tope = 50, rango = null) {
  const leer = (campos, sinBorradas) => {
    let q = supabase.from("ordenes_gasoil").select(campos);
    if (sinBorradas) q = q.is("borrada_en", null);
    if (rango?.desde) q = q.gte("creado_en", rango.desde);
    if (rango?.hasta) q = q.lt("creado_en", rango.hasta);
    return q.order("creado_en", { ascending: false }).limit(tope);
  };
  let { data, error } = await leer(CAMPOS_EDITADOS, true);
  if (error && faltaEditados(error)) ({ data, error } = await leer(CAMPOS, true));
  if (error && faltaBorrada(error)) ({ data, error } = await leer(CAMPOS, false));
  if (error) {
    console.warn("[orden-gasoil] historial:", error.message);
    return { filas: [], error: "No se pudo consultar." };
  }
  const filas = data || [];
  // Cada orden con su último pedido de cambio de litros, si tiene.
  const ultimo = await cambios(filas.map((f) => f.id));
  return { filas: filas.map((f) => (ultimo[f.id] ? { ...f, cambio: ultimo[f.id] } : f)), error: "" };
}

/* ¿La base todavía no tiene `tanque_completo` (la migración del ticket 449
 * sin aplicar)? 42703 = la columna no existe (Postgres); PGRST204 = PostgREST
 * no la ve. Sólo se pregunta cuando la fila la lleva: los litros con número
 * no la mandan y se guardan como siempre. */
const faltaTanqueCompleto = (error) =>
  ["42703", "PGRST204"].includes(error?.code) ||
  /column.*tanque_completo|tanque_completo.*column/i.test(error?.message || "");

/* Guarda la orden generada. `usuario` lo pone la base (`mi_mail()`). Si no
 * se pudo, `error` es el aviso para la pantalla. */
export async function guardar(fila) {
  const { data, error } = await supabase.from("ordenes_gasoil").insert(fila).select().single();
  if (error) {
    console.warn("[orden-gasoil] guardar:", error.message);
    const aviso =
      fila?.tanque_completo && faltaTanqueCompleto(error)
        ? "El correo está armado, pero no quedó en el historial: para guardar «Tanque completo» falta una actualización de la base."
        : "No se pudo guardar en el historial.";
    return { ok: false, fila: null, error: aviso };
  }
  return { ok: true, fila: data };
}

/* Borra una orden (ticket 448): la marca con `borrada_en`/`borrada_por` por la
 * RPC `borrar_orden_gasoil`, que exige `cargar.satelital`. No se elimina: una
 * orden de gas oil es plata, y así se puede deshacer a mano. */
export async function borrar(id) {
  const { data, error } = await supabase.rpc("borrar_orden_gasoil", { p_id: id });
  if (error) {
    console.warn("[orden-gasoil] borrar:", error.message);
    let msj = "No se pudo borrar. Probá de nuevo.";
    if (faltaFuncion(error)) msj = "Todavía no se puede borrar: falta una actualización de la base. Probá más tarde.";
    else if (error.code === "42501") msj = "No tenés permiso para borrar órdenes de gas oil.";
    return { ok: false, fila: null, error: msj };
  }
  return { ok: true, fila: data, error: "" };
}

/* El último pedido de cambio de litros de cada orden, por id de orden. Sin la
 * tabla (migración sin aplicar) o si no se pudo leer, vacío: el historial se
 * ve igual, sin los pedidos. */
export async function cambios(ids) {
  if (!ids?.length) return {};
  let data;
  try {
    const r = await supabase
      .from("ordenes_gasoil_cambios")
      .select(CAMPOS_CAMBIO)
      .in("orden_id", ids)
      .order("pedido_en", { ascending: false });
    if (r.error) throw r.error;
    data = r.data;
  } catch (error) {
    console.warn("[orden-gasoil] cambios:", error?.message);
    return {};
  }
  const ultimo = {};
  for (const c of data || []) if (!ultimo[c.orden_id]) ultimo[c.orden_id] = c;
  return ultimo;
}

const errorCambio = (error, nombre) => {
  if (faltaFuncion(error, nombre)) return "Todavía no se puede pedir el cambio. Probá más tarde.";
  if (error?.code === "42501") return "No tenés permiso para cambiar órdenes de gas oil.";
  if (error?.code === "23505") return "Esa orden ya tiene un cambio esperando autorización.";
  if (error?.code === "55000") return "Esa orden está borrada.";
  if (error?.code === "P0002") return "Esa orden ya no está.";
  if (error?.code === "22023") return error.message || "Revisá los litros y el motivo.";
  return "No se pudo pedir el cambio. Probá de nuevo.";
};

/* Pedir el cambio de litros de una orden ya enviada (Lautaro, 21/10: «me
 * pidieron mas litros porque no llegaban»). Nico, 21/10: «que puedan
 * cambiarlo con mi autorización». Así que esto NO cambia la orden: deja el
 * pedido pendiente (`pedir_cambio_litros_orden_gasoil`) y FlotaBot le manda a
 * Nico un WhatsApp con «Autorizar» y «Rechazar». El correo a la estación ya
 * salió y no cambia. */
export async function pedirCambio(id, litros, motivo, nombre) {
  const { data, error } = await supabase.rpc("pedir_cambio_litros_orden_gasoil", {
    p_id: id,
    p_litros: litros,
    p_motivo: motivo,
    p_nombre: nombre || null,
  });
  if (error) {
    console.warn("[orden-gasoil] pedir cambio:", error.message);
    return { ok: false, cambio: null, error: errorCambio(error, "pedir_cambio_litros_orden_gasoil") };
  }
  return { ok: true, cambio: data, error: "" };
}

/* Retirar un pedido que todavía no se resolvió. */
export async function anularCambio(idCambio) {
  const { data, error } = await supabase.rpc("anular_cambio_litros_orden_gasoil", { p_cambio: idCambio });
  if (error) {
    console.warn("[orden-gasoil] anular cambio:", error.message);
    return { ok: false, cambio: null, error: "No se pudo retirar el pedido." };
  }
  return { ok: true, cambio: data, error: "" };
}

/* Los tickets de carga que mandaron los choferes (Nico, 28/10, ticket 545),
 * desde `desde` (ISO): la foto y lo que FlotaBot leyó de ella. El cruce con
 * cada orden lo hace `fotosDeLaOrden` (shared/ordenGasoil). Si no se pudo
 * leer, `error` y vacío: el historial se ve igual, sin las fotos. */
const CAMPOS_TICKET =
  "id,foto,chofer_nombre,dni_chofer,interno,recibida_en,estado,es_ticket,fecha_hora,establecimiento,localidad,litros,dni_ticket,interno_ticket,correccion,creado_en";

export async function ticketsCombustible(desde) {
  if (!desde) return { filas: [], error: "" };
  const { data, error } = await supabase
    .from("tickets_combustible")
    .select(CAMPOS_TICKET)
    .gte("creado_en", desde)
    .order("creado_en", { ascending: false })
    .limit(1000);
  if (error) {
    console.warn("[orden-gasoil] tickets de carga:", error.message);
    return { filas: [], error: "No se pudieron consultar las fotos de los choferes." };
  }
  return { filas: data || [], error: "" };
}

/* La foto del ticket: vive en el bucket privado `novedades` (la deja mirar a
 * quien tiene `cargar.satelital` la política de la migración 20261028150000). */
export const BUCKET_TICKETS = "novedades";
